import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { format } from 'date-fns';
import { toast } from 'sonner';
import {
  ArrowLeft, RotateCcw, Package, Calendar, Clock, MapPin, Receipt, Loader2, ShoppingBag, CheckCircle2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { getImageUrl } from '../utils/imageUrl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusLabel = {
  pending_payment: { text: 'Awaiting payment', color: 'bg-stone-100 text-stone-700' },
  confirmed: { text: 'Confirmed', color: 'bg-blue-100 text-blue-700' },
  preparing: { text: 'Being prepared', color: 'bg-amber-100 text-amber-700' },
  ready: { text: 'Ready for collection', color: 'bg-green-100 text-green-700' },
  collected: { text: 'Collected', color: 'bg-stone-200 text-stone-600' },
  cancelled: { text: 'Cancelled', color: 'bg-red-100 text-red-700' },
};

export default function OrderDetailPage() {
  const { orderId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reordering, setReordering] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    const load = async () => {
      try {
        const res = await axios.get(`${API}/orders/${orderId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setOrder(res.data);
      } catch (e) {
        toast.error('Order not found');
        navigate('/account');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [orderId, token, navigate]);

  const handleReorder = async () => {
    setReordering(true);
    try {
      const res = await axios.post(
        `${API}/orders/${orderId}/reorder`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { added, skipped } = res.data;
      if (added > 0) {
        toast.success(`${added} item${added > 1 ? 's' : ''} added to your basket`);
        if (skipped?.length) {
          toast.warning(`Skipped: ${skipped.join(', ')}`);
        }
        navigate('/cart');
      } else {
        toast.error('Could not reorder — items no longer available');
      }
    } catch (e) {
      toast.error('Failed to reorder');
    } finally {
      setReordering(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <div className="pt-32 pb-20 max-w-4xl mx-auto px-4 sm:px-6">
          <div className="h-8 bg-stone-100 animate-pulse rounded-full w-40 mb-6" />
          <div className="h-40 bg-stone-100 animate-pulse rounded-2xl mb-4" />
          <div className="h-60 bg-stone-100 animate-pulse rounded-2xl" />
        </div>
      </div>
    );
  }

  if (!order) return null;

  const status = statusLabel[order.status] || { text: order.status, color: 'bg-stone-100 text-stone-700' };
  const createdDate = order.created_at ? format(new Date(order.created_at), 'd MMM yyyy, HH:mm') : '';
  const pickupDate = order.pickup_date && order.pickup_time
    ? `${format(new Date(order.pickup_date), 'EEEE, d MMM yyyy')} at ${order.pickup_time}`
    : null;
  const isCollected = order.status === 'collected' || order.status === 'cancelled';

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <div className="pt-24 sm:pt-32 pb-20 max-w-4xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Link
            to="/account"
            className="inline-flex items-center gap-2 text-stone-600 hover:text-[#1a1a1a] mb-6"
            data-testid="order-detail-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back to account
          </Link>

          {/* Header card */}
          <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-8 mb-4 border border-stone-100">
            <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
              <div>
                <p className="text-xs uppercase tracking-widest text-stone-400">Order</p>
                <h1 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-bold text-[#1a1a1a]" data-testid="order-number">
                  #{order.order_number || order.id?.slice(0, 8)}
                </h1>
                <p className="text-sm text-stone-500 mt-1">{createdDate}</p>
              </div>
              <span className={`px-4 py-1.5 rounded-full text-xs font-semibold ${status.color}`}>
                {status.text}
              </span>
            </div>

            {/* Pickup info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              {pickupDate && (
                <div className="flex items-start gap-2.5 bg-stone-50 rounded-xl p-3">
                  <Calendar className="w-4 h-4 text-[#8D6E63] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-stone-500 text-xs">Collection</p>
                    <p className="font-medium text-[#1a1a1a]">{pickupDate}</p>
                  </div>
                </div>
              )}
              <div className="flex items-start gap-2.5 bg-stone-50 rounded-xl p-3">
                <MapPin className="w-4 h-4 text-[#8D6E63] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-stone-500 text-xs">Pickup at</p>
                  <p className="font-medium text-[#1a1a1a]">Mad Brothers Bakery, Dublin 7</p>
                </div>
              </div>
            </div>
          </div>

          {/* Items */}
          <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-8 mb-4 border border-stone-100">
            <h2 className="font-['Playfair_Display'] text-xl font-bold text-[#1a1a1a] mb-4 flex items-center gap-2">
              <Package className="w-5 h-5 text-[#8D6E63]" /> Items ({order.items?.length || 0})
            </h2>
            <div className="space-y-4">
              {order.items?.map((item, idx) => {
                const img = item.image && getImageUrl(item.image);
                return (
                  <div key={idx} className="flex gap-3 sm:gap-4 pb-4 border-b border-stone-100 last:border-0 last:pb-0" data-testid={`order-detail-item-${idx}`}>
                    {img ? (
                      <img
                        src={img}
                        alt={item.product_name}
                        loading="lazy"
                        className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl object-cover border border-stone-100 flex-shrink-0"
                      />
                    ) : (
                      <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl bg-stone-100 flex items-center justify-center flex-shrink-0">
                        <ShoppingBag className="w-6 h-6 text-stone-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between gap-3 items-start">
                        <div className="min-w-0">
                          <p className="font-medium text-[#1a1a1a]">{item.quantity}x {item.product_name}</p>
                          <p className="text-xs text-stone-500 mt-0.5">
                            {item.size_name && <span>{item.size_name}</span>}
                            {item.size_name && item.flavor_name && <span> • </span>}
                            {item.flavor_name && <span>{item.flavor_name}</span>}
                          </p>
                        </div>
                        <p className="font-semibold text-[#8D6E63] whitespace-nowrap">€{item.total_price?.toFixed(2)}</p>
                      </div>
                      {item.custom_text_full && (
                        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 mt-2 font-medium">
                          🎂 {item.custom_text_full}
                        </p>
                      )}
                      {item.cake_message && (
                        <p className="text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded-lg px-2.5 py-1.5 mt-1.5">
                          <span className="font-medium">Cake message:</span> "{item.cake_message}"
                        </p>
                      )}
                      {item.special_request && (
                        <p className="text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded-lg px-2.5 py-1.5 mt-1.5">
                          <span className="font-medium">Special request:</span> {item.special_request}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Totals */}
            <div className="mt-5 pt-4 border-t border-stone-100 text-sm space-y-1.5">
              <div className="flex justify-between text-stone-600">
                <span>Subtotal</span>
                <span>€{order.subtotal?.toFixed(2) ?? order.total?.toFixed(2)}</span>
              </div>
              {(order.discount && order.discount > 0) ? (
                <div className="flex justify-between text-green-700">
                  <span>Discount {order.coupon_code ? `(${order.coupon_code})` : ''}</span>
                  <span>-€{order.discount.toFixed(2)}</span>
                </div>
              ) : null}
              <div className="flex justify-between text-lg font-bold text-[#1a1a1a] pt-2 border-t border-stone-100">
                <span>Total paid</span>
                <span>€{order.total?.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Contact + Receipt */}
          <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-8 mb-4 border border-stone-100">
            <h2 className="font-['Playfair_Display'] text-lg font-bold text-[#1a1a1a] mb-3 flex items-center gap-2">
              <Receipt className="w-5 h-5 text-[#8D6E63]" /> Contact for this order
            </h2>
            <div className="text-sm text-stone-600 space-y-1">
              {order.contact_name && <p><span className="text-stone-400">Name:</span> {order.contact_name}</p>}
              {order.contact_email && <p><span className="text-stone-400">Email:</span> {order.contact_email}</p>}
              {order.contact_phone && <p><span className="text-stone-400">Phone:</span> {order.contact_phone}</p>}
            </div>
          </div>

          {/* CTAs */}
          <div className="bg-gradient-to-br from-[#FFF8E1] via-[#FFF3C4] to-[#FFE4A0] rounded-2xl p-6 sm:p-8 border-2 border-[#D4AF37]/40 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="font-['Playfair_Display'] text-xl font-bold text-[#1a1a1a] mb-1 flex items-center gap-2">
                  {isCollected ? <CheckCircle2 className="w-5 h-5 text-green-700" /> : <Clock className="w-5 h-5 text-[#8D6E63]" />}
                  Loved this order?
                </h3>
                <p className="text-sm text-stone-700">
                  Add the same items to your basket with one click.
                </p>
              </div>
              <Button
                onClick={handleReorder}
                disabled={reordering}
                className="bg-[#1a1a1a] hover:bg-[#1a1a1a]/90 text-white rounded-full px-6 h-12 font-semibold"
                data-testid="reorder-btn"
              >
                {reordering ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Adding...</>
                ) : (
                  <><RotateCcw className="w-4 h-4 mr-2" /> Order again</>
                )}
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
